import React, { ChangeEvent, useMemo, useCallback, memo } from "react";
import { filter } from "lodash";
import { TColumn } from "../../lib/utils/getColumnsFromDataDictonary";
import { OmniDropDownInput } from "../../omni/dropdown";

interface ISelectProps {
    isLoading: boolean;
    placeHolder?: string;
    onChange: (event: ChangeEvent<HTMLSelectElement>) => void;
    columnValue: number;
    columns: TColumn[];
    style?: object;
    isMetricSelection: boolean;
}

const SelectWithCommonFieldsTopComponent: React.FC<ISelectProps> = ({
    isLoading = false,
    placeHolder = "Select a value",
    onChange,
    style = {},
    columnValue,
    columns,
    isMetricSelection = false,
}) => {
    // ? Memoize filtered columns
    const { commonColumns, otherColumns } = useMemo(() => {
        const common = filter(
            columns,
            ({ IsMetric, IsCommon }) => IsMetric === isMetricSelection && IsCommon === true
        );
        const other = filter(
            columns,
            ({ IsMetric, IsCommon }) => IsMetric === isMetricSelection && IsCommon === false
        );
        return { commonColumns: common, otherColumns: other };
    }, [columns, isMetricSelection]);

    const totalColumnCount = useMemo(() =>
        commonColumns.length + otherColumns.length,
        [commonColumns.length, otherColumns.length]
    );

    // ? Memoize options array
    const options = useMemo(() => {
        const opts = [
            {
                id: "-1",
                value: isLoading ? "Loading..." : placeHolder,
                disabled: true,
            },
            ...commonColumns.map(({ DisplayName, TableName }, i) => ({
                id: i.toString(),
                value: `${DisplayName} (${TableName})`,
            })),
        ];

        if (otherColumns.length > 0) {
            opts.push({
                id: "separator",
                value: "-----------------",
                disabled: true,
            });
            opts.push(
                ...otherColumns.map(({ DisplayName, TableName }, i) => ({
                    id: (i + commonColumns.length).toString(),
                    value: `${DisplayName} (${TableName})`,
                }))
            );
        }

        return opts;
    }, [commonColumns, otherColumns, isLoading, placeHolder]);

    // ? Memoize selectable options
    const selectableOptions = useMemo(() =>
        options.filter((opt) => !opt.disabled && opt.id !== "separator"),
        [options]
    );

    // ? Memoize selected value
    const value = useMemo(() => {
        const selectedOption = selectableOptions.find(
            (opt) => opt.id === columnValue.toString()
        );
        return selectedOption ? [selectedOption] : [];
    }, [selectableOptions, columnValue]);

    // ? Memoize label tooltip
    const labelTooltip = useMemo(() =>
        value.length > 0 ? value[0].value : '',
        [value]
    );

    // ? Memoize onChange handler
    const handleValueChange = useCallback((e: CustomEvent) => {
        const selectedId = e.detail?.id ?? "-1";
        onChange({
            target: { value: selectedId },
        } as unknown as ChangeEvent<HTMLSelectElement>);
    }, [onChange]);

    return (
        <OmniDropDownInput
            className="text-capitalize w-100 dropdown-lg"
            value={value}
            options={options}
            onValueChange={handleValueChange}
            placeholder={isLoading ? "Loading..." : placeHolder}
            disabled={isLoading || !totalColumnCount}
            hidefooter
            searchindropdown
            labelTooltip={labelTooltip}
            tooltipOnOverflow
            preventLastRemoval
        />
    );
};

export const SelectWithCommonFieldsTop = memo(SelectWithCommonFieldsTopComponent);